import { addDays, format, parseISO } from 'date-fns'
import { useState } from 'react'

import type { TreatmentWithRelations } from '@snootsvet/shared'
import { formatCalendarDate, REGIONS_BY_ID } from '@snootsvet/shared'

import { FormattedCalendarDate, useViewerFormat } from '@/frontend/components/formatted-datetime'
import { Button } from '@/frontend/components/ui/button'
import { ConfirmButton } from '@/frontend/components/ui/confirm-button'
import { ReminderFields } from '@/frontend/features/patient/reminder-fields'
import {
  type TreatmentReminderTarget,
  vaccineTargetsForSpecies,
} from '@/frontend/features/patient/reminder-targets'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useAddTreatmentReminder } from '@/frontend/hooks/api/use-add-treatment-reminder'
import { useCancelTreatmentReminder } from '@/frontend/hooks/api/use-cancel-treatment-reminder'

/** Days out below which an upcoming reminder counts as "due soon". */
const DUE_SOON_DAYS = 7

type ReminderDueTier = 'overdue' | 'due-soon' | 'upcoming'

/** Due-date colour by urgency: green upcoming, amber within a week, red day-of/overdue.
 *  Explicit shades (not the `--warning` token, which is a light bg tint) so the text stays
 *  legible on the card, with dark-mode variants. */
const REMINDER_DUE_TIER_CLASS: Record<ReminderDueTier, string> = {
  upcoming: 'text-success-fg',
  'due-soon': 'text-warning-fg',
  overdue: 'text-destructive-fg',
}

/**
 * Buckets a bare calendar due date (`YYYY-MM-DD`) by urgency — overdue today or earlier,
 * due-soon within the next week, upcoming beyond that. Both operands are `YYYY-MM-DD`, so
 * the string compares are calendar-date compares.
 *
 * Resolved against the viewer's local date rather than the clinic's timezone: this only
 * picks a text colour, so a day's drift at the boundary doesn't matter.
 */
function getReminderDueTier(dueAt: string): ReminderDueTier {
  const today = format(new Date(), 'yyyy-MM-dd')
  if (dueAt <= today) return 'overdue'
  return dueAt <= format(addDays(new Date(), DUE_SOON_DAYS), 'yyyy-MM-dd') ? 'due-soon' : 'upcoming'
}

interface TreatmentRemindersProps {
  treatment: TreatmentWithRelations
  patientId: string
  patientSpecies: string
  /** When rendered inside a consultation, refresh that consultation's cache on add/cancel. */
  consultationId?: string
  /** The caller owns the add form's visibility; this renders the form while open. */
  addOpen?: boolean
  onAddOpenChange?: (open: boolean) => void
}

/** Whether this vaccination has a booster reminder left that the viewer could add. */
export function canAddReminder(
  treatment: TreatmentWithRelations,
  patientSpecies: string,
  mode: 'disease-list' | 'product-name'
): boolean {
  const activeTargets = new Set(treatment.reminders.map((r) => r.target))
  const productName = treatment.product?.name ?? null
  if (mode === 'product-name') return productName != null && !activeTargets.has(productName)
  return vaccineTargetsForSpecies(patientSpecies).some((t) => !activeTargets.has(t))
}

/**
 * A vaccine's active booster reminders (inline, overdue in red, cancellable), plus the add
 * form while the caller has it open. Shared by the Vaccinations card and the consultation
 * treatment rows.
 */
export function TreatmentReminders({
  treatment,
  patientId,
  patientSpecies,
  consultationId,
  addOpen,
  onAddOpenChange,
}: TreatmentRemindersProps) {
  const { region } = useViewerRegion()
  const fmt = useViewerFormat()
  const cancelReminder = useCancelTreatmentReminder({ patientId, consultationId })
  const reminders = treatment.reminders
  const adding = !treatment.cancelledAt && !!addOpen && !!onAddOpenChange

  if (reminders.length === 0 && !adding) return null

  return (
    <>
      {reminders.map((reminder) => (
        <div key={reminder.id} className="flex items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">
            Next {reminder.target} booster · due{' '}
            <span className={REMINDER_DUE_TIER_CLASS[getReminderDueTier(reminder.dueAt)]}>
              <FormattedCalendarDate value={reminder.dueAt} />
            </span>
          </p>
          <ConfirmButton
            type="button"
            variant="ghost"
            disabled={cancelReminder.isPending}
            confirmPending={cancelReminder.isPending}
            heading="Cancel reminder?"
            description={`This will cancel the ${reminder.target} booster reminder due ${formatCalendarDate(reminder.dueAt, fmt)}.`}
            confirmLabel="Cancel reminder"
            dialogColor="destructive"
            onConfirm={() => cancelReminder.mutate(reminder.id)}
          >
            Cancel
          </ConfirmButton>
        </div>
      ))}
      {adding && (
        <AddReminderControl
          treatment={treatment}
          // The viewer's region decides the target vocabulary: UK names the vaccine itself,
          // US picks a disease off the species list.
          mode={REGIONS_BY_ID[region].vaccineTargetMode}
          patientSpecies={patientSpecies}
          patientId={patientId}
          consultationId={consultationId}
          onClose={() => onAddOpenChange(false)}
        />
      )}
    </>
  )
}

/**
 * Form for adding a booster reminder to a single vaccination, with presets anchored to the
 * shot's administration date as the viewer sees it. `mode` decides the target vocabulary:
 * `disease-list` offers the species diseases minus those already covered; `product-name` uses
 * the vaccine's own name and renders no dropdown. Renders nothing when there's nothing left
 * to add.
 */
function AddReminderControl({
  treatment,
  mode,
  patientSpecies,
  patientId,
  consultationId,
  onClose,
}: {
  treatment: TreatmentWithRelations
  /** Target vocabulary — the caller resolves it, this only reads it. */
  mode: 'disease-list' | 'product-name'
  patientSpecies: string
  patientId: string
  consultationId?: string
  onClose: () => void
}) {
  const [target, setTarget] = useState<TreatmentReminderTarget | null>(null)
  const [dueAt, setDueAt] = useState<string | null>(null)
  const { mutateAsync: addReminder, isPending } = useAddTreatmentReminder({
    patientId,
    consultationId,
  })

  const activeTargets = new Set(treatment.reminders.map((r) => r.target))
  // UK: the target is the vaccine's product name, so there's nothing to pick and `options` is
  // undefined. US: the vet picks from the species list, minus targets this shot already covers.
  const productName = treatment.product?.name ?? null
  const options =
    mode === 'product-name'
      ? undefined
      : vaccineTargetsForSpecies(patientSpecies).filter((t) => !activeTargets.has(t))

  if (!canAddReminder(treatment, patientSpecies, mode)) return null

  const onAdd = async () => {
    if (!effectiveTarget || !dueAt) return
    try {
      await addReminder({ treatmentId: treatment.id, target: effectiveTarget, dueAt })
      onClose()
    } catch (e) {
      console.error('Failed to add treatment reminder:', e)
    }
  }

  // Anchor presets to the shot's calendar date as the viewer sees it. Round-tripping through
  // YYYY-MM-DD drops the time, so "+1 year" lands on a date rather than an instant.
  const shotAnchor = parseISO(format(new Date(treatment.createdAt), 'yyyy-MM-dd'))

  // UK's target is fixed to the product name; US's is the vet's dropdown selection.
  const effectiveTarget = options ? target : productName

  return (
    <div className="flex flex-wrap items-center gap-2">
      <ReminderFields
        options={options}
        target={effectiveTarget}
        dueAt={dueAt}
        anchor={shotAnchor}
        onTargetChange={setTarget}
        onDueAtChange={setDueAt}
      />
      <Button type="button" disabled={!effectiveTarget || !dueAt || isPending} onClick={onAdd}>
        Add
      </Button>
      <Button type="button" variant="ghost" onClick={onClose}>
        Cancel
      </Button>
    </div>
  )
}
